import {TestObservation} from '../types';
import {
    classifyObservation,
    dependencyCodes,
    ObservationKind,
    observationKindDisplay,
} from './classifyObservation';

// FNP-861 — "a cascade must read as ONE bug".
//
// Classification (classifyObservation) tells you what a single row means. That is
// not enough on its own: three greyed-out "Skipped — blocked" rows scattered among
// unrelated observations still don't tell the triager WHICH failure blocked them.
// This module does the second half — it attaches each dependency-skip to the actual
// row that caused it, so the four rows of the known dev cascade
//
//     FAIL     user.sign-in
//     SKIPPED  email.alias-list   dependedOn: ["user.sign-in"]
//     SKIPPED  email.message-list dependedOn: ["user.sign-in"]
//     SKIPPED  email.alias-create dependedOn: ["user.sign-in"]
//
// render as ONE entry — the sign-in failure — carrying three visibly subordinate
// consequences, rather than four sibling rows of equal weight.
//
// MATCHING. A dependency-skip names the actionCode(s) it waited on, not their ids,
// so the root is resolved by actionCode WITHIN THE SAME SYNTHETIC USER. Two users
// failing user.sign-in in the same page are two separate cascades — collapsing
// across users would invent a bug that does not exist. When the named root is not
// in the loaded page (paging, or a filter that excluded it), the row stays a
// top-level entry and says what it was waiting on: never silently drop a row
// because its parent is missing.

export type CascadeChild = {
    record: TestObservation,
    kind: ObservationKind,
};

export type CascadeEntry = {
    record: TestObservation,
    kind: ObservationKind,
    // Rows that deferred to this one. Empty for the ordinary standalone row.
    children: CascadeChild[],
    // Set when this row is a dependency-skip whose root is NOT in the current page.
    // The UI shows what it was waiting on instead of pretending it is independent.
    orphanedDependencies: string[],
};

const observedAtDesc = (a: TestObservation, b: TestObservation) =>
    (b.observedAt ?? '').localeCompare(a.observedAt ?? '');

export const buildCascades = (records: TestObservation[]): CascadeEntry[] => {

    // actionCode -> the row that could be a root, keyed per synthetic user.
    // Only FAIL rows can root a cascade: a dependency-skip defers to something that
    // went wrong, and a PASS by definition did not block anything.
    const rootsByUserAndCode = new Map<string, TestObservation>();
    const key = (syntheticUserId: string, actionCode: string) => `${syntheticUserId} ${actionCode}`;

    for (const record of records) {
        if (classifyObservation(record) === ObservationKind.FAIL) {
            const k = key(record.syntheticUserId, record.actionCode);
            const existing = rootsByUserAndCode.get(k);
            // Most recent failure wins if the same action failed twice in the page.
            if (!existing || observedAtDesc(record, existing) < 0) {
                rootsByUserAndCode.set(k, record);
            }
        }
    }

    const childrenByRootId = new Map<string, CascadeChild[]>();
    const claimedChildIds = new Set<string>();
    const orphansById = new Map<string, string[]>();

    for (const record of records) {
        const kind = classifyObservation(record);
        if (kind !== ObservationKind.DEPENDENCY_SKIP) {
            continue;
        }

        const codes = dependencyCodes(record);
        // Attach to the first named dependency that resolves to a root in this page.
        const root = codes
            .map(code => rootsByUserAndCode.get(key(record.syntheticUserId, code)))
            .find((candidate): candidate is TestObservation => typeof candidate !== 'undefined');

        if (root && root.id !== record.id) {
            const siblings = childrenByRootId.get(root.id) ?? [];
            siblings.push({
                             record,
                             kind
                         });
            childrenByRootId.set(root.id, siblings);
            claimedChildIds.add(record.id);
        } else {
            // Root not on this page — keep the row visible and say what it awaited.
            orphansById.set(record.id, codes);
        }
    }

    const entries: CascadeEntry[] = records
        // A claimed child is rendered underneath its root, never also at top level.
        .filter(record => !claimedChildIds.has(record.id))
        .map(record => {
            const children = (childrenByRootId.get(record.id) ?? []).sort((a, b) =>
                observedAtDesc(a.record, b.record));
            return {
                record,
                kind: classifyObservation(record),
                children,
                orphanedDependencies: orphansById.get(record.id) ?? [],
            };
        });

    // Severity first so the root cause outranks the noise, then newest-first within
    // a severity band. A cascade root is a FAIL (severity 0), so it lands at the top
    // and carries its consequences with it — which is the whole point.
    return entries.sort((a, b) => {
        const severityDelta = observationKindDisplay(a.kind).severity - observationKindDisplay(b.kind).severity;
        if (severityDelta !== 0) {
            return severityDelta;
        }
        return observedAtDesc(a.record, b.record);
    });
};

// Headline counts for the triage summary. Counted over the RAW rows (not the
// cascade entries) so the numbers still reconcile with the row count the API
// returned — a dependent that was folded under a root is still an observation.
export type ObservationSummary = {
    total: number,
    failed: number,
    passedUnverified: number,
    blocked: number,
    unimplemented: number,
};

export const summarizeObservations = (records: TestObservation[]): ObservationSummary => {
    const summary: ObservationSummary = {
        total: records.length,
        failed: 0,
        passedUnverified: 0,
        blocked: 0,
        unimplemented: 0,
    };

    for (const record of records) {
        switch (classifyObservation(record)) {
            case ObservationKind.FAIL:
                summary.failed += 1;
                break;
            case ObservationKind.PASS_UNVERIFIED:
                summary.passedUnverified += 1;
                break;
            case ObservationKind.DEPENDENCY_SKIP:
                summary.blocked += 1;
                break;
            case ObservationKind.UNIMPLEMENTED:
                summary.unimplemented += 1;
                break;
            default:
                break;
        }
    }

    return summary;
};
